import { useRef, useState, type JSX } from 'react'
import { Button } from 'primereact/button'
import { InputSwitch } from 'primereact/inputswitch'
import { confirmDialog } from 'primereact/confirmdialog'
import { z } from 'zod'
import type { ConditionTree } from '@kc3/conversations'

import PageHeader from '../../../components/admin/PageHeader.js'
import ResourceTable, {
  type ColumnDef,
} from '../../../components/admin/ResourceTable.js'
import ResourceDrawer from '../../../components/admin/ResourceDrawer.js'
import {
  ZodForm,
  FormText,
  FormNumber,
  FormSwitch,
  FormDropdown,
  FormPassword,
} from '../../../components/forms/index.js'
import { useAdminResource } from '../../../hooks/useAdminApi.js'
import { useRuleFields } from '../../../hooks/useRuleFields.js'
import RuleBuilder from '../../../components/admin/conversation-engine/RuleBuilder.js'
import EscalationActionPicker, {
  type EscalationAction,
} from '../../../components/admin/conversation-engine/EscalationActionPicker.js'
import ThresholdVisualization from '../../../components/admin/conversation-engine/ThresholdVisualization.js'
import Select from '../../../components/ui/Select.js'

// ---------------------------------------------------------------------------
// Types — derived from apps/api/src/routes/conversations/admin/escalation-rules.ts
// (escalation_rules table). triggerType is one of three values; the two breach
// triggers carry a `*ThresholdPct`, custom_condition carries a `conditions` tree.
// ---------------------------------------------------------------------------

type TriggerType =
  | 'first_response_breach'
  | 'resolution_breach'
  | 'custom_condition'

export interface EscalationRule {
  id: string
  name: string
  triggerType: TriggerType
  firstResponseThresholdPct?: number
  resolutionThresholdPct?: number
  conditions?: ConditionTree
  actions?: EscalationAction[]
  appliesToSlaPolicyId?: string
  isActive: boolean
  sortOrder: number
  // Webhook config (S2 T13) — secret is write-only; backend returns hasWebhookSecret boolean
  webhookUrl?: string | null
  hasWebhookSecret?: boolean
}

interface SlaPolicy {
  id: string
  name: string
  firstResponseMinutes: number
  resolutionMinutes: number
}

const TRIGGER_OPTIONS: ReadonlyArray<{ label: string; value: TriggerType }> = [
  { label: 'First response breach', value: 'first_response_breach' },
  { label: 'Resolution breach', value: 'resolution_breach' },
  { label: 'Custom condition', value: 'custom_condition' },
]

const TRIGGER_LABELS: Record<TriggerType, string> = {
  first_response_breach: 'First response breach',
  resolution_breach: 'Resolution breach',
  custom_condition: 'Custom condition',
}

// Stable module-level constants (avoid fresh refs each render).
const EMPTY_CONDITIONS: ConditionTree = { operator: 'AND', conditions: [] }
const EMPTY_ACTIONS: EscalationAction[] = []

const UNSCOPED = 'unscoped'

const RuleSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  appliesToSlaPolicyId: z.string(),
  triggerType: z.enum([
    'first_response_breach',
    'resolution_breach',
    'custom_condition',
  ]),
  thresholdPct: z.number().int().min(1).max(100),
  isActive: z.boolean(),
  sortOrder: z.number().int().min(0).max(32767),
  // Webhook section (S2 T13) — secret is write-only
  webhookUrl: z.string().url().optional().or(z.literal('')),
  webhookSecret: z.string().optional(),
})

type RuleFormValues = z.infer<typeof RuleSchema>

function isThresholdTrigger(
  t: TriggerType,
): t is 'first_response_breach' | 'resolution_breach' {
  return t === 'first_response_breach' || t === 'resolution_breach'
}

function summariseActions(actions: EscalationAction[] | undefined): string {
  if (!actions || actions.length === 0) return '—'
  return `${actions.length} action${actions.length === 1 ? '' : 's'}`
}

function thresholdOf(rule: EscalationRule): number | null {
  if (rule.triggerType === 'first_response_breach') {
    return rule.firstResponseThresholdPct ?? null
  }
  if (rule.triggerType === 'resolution_breach') {
    return rule.resolutionThresholdPct ?? null
  }
  return null
}

export default function EscalationRulesPage(): JSX.Element {
  const { items, loading, create, update, remove } =
    useAdminResource<EscalationRule>('/admin/conversations/escalation-rules', {
      responseKey: 'rows',
    })
  const { items: slaItems } = useAdminResource<SlaPolicy>(
    '/admin/conversations/sla-policies',
    { responseKey: 'rows' },
  )
  const { fields } = useRuleFields('escalation')

  const slaPolicies = slaItems

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<EscalationRule | null>(null)
  const [copySource, setCopySource] = useState<EscalationRule | null>(null)
  const [conditions, setConditions] = useState<ConditionTree>(EMPTY_CONDITIONS)
  const [actions, setActions] = useState<EscalationAction[]>(EMPTY_ACTIONS)
  const [slaFilter, setSlaFilter] = useState<string>('')
  const [triggerFilter, setTriggerFilter] = useState<string>('')
  const formRef = useRef<HTMLFormElement>(null)

  function openCreate(): void {
    setEditing(null)
    setCopySource(null)
    setConditions(EMPTY_CONDITIONS)
    setActions(EMPTY_ACTIONS)
    setDrawerOpen(true)
  }

  function openEdit(row: EscalationRule): void {
    setEditing(row)
    setCopySource(null)
    setConditions(row.conditions ?? EMPTY_CONDITIONS)
    setActions((row.actions as EscalationAction[]) ?? EMPTY_ACTIONS)
    setDrawerOpen(true)
  }

  function openCopy(row: EscalationRule): void {
    setEditing(null)
    setCopySource(row)
    setConditions(row.conditions ?? EMPTY_CONDITIONS)
    setActions((row.actions as EscalationAction[]) ?? EMPTY_ACTIONS)
    setDrawerOpen(true)
  }

  function closeDrawer(): void {
    setDrawerOpen(false)
    setEditing(null)
    setCopySource(null)
  }

  async function handleSubmit(data: RuleFormValues): Promise<void> {
    const base: Record<string, unknown> = {
      name: data.name.trim(),
      triggerType: data.triggerType,
      isActive: data.isActive,
      sortOrder: data.sortOrder,
      actions,
      ...(data.appliesToSlaPolicyId
        ? { appliesToSlaPolicyId: data.appliesToSlaPolicyId }
        : {}),
    }
    // Webhook section — only include if provided
    if (data.webhookUrl) base.webhookUrl = data.webhookUrl
    if (data.webhookSecret) base.webhookSecret = data.webhookSecret

    let payload: Record<string, unknown>
    if (data.triggerType === 'first_response_breach') {
      payload = { ...base, firstResponseThresholdPct: data.thresholdPct }
    } else if (data.triggerType === 'resolution_breach') {
      payload = { ...base, resolutionThresholdPct: data.thresholdPct }
    } else {
      payload = { ...base, conditions }
    }
    if (editing) {
      await update(editing.id, payload)
    } else {
      await create(payload)
    }
    closeDrawer()
  }

  function handleDelete(row: EscalationRule): void {
    confirmDialog({
      message: `Permanently delete "${row.name}"? This cannot be undone.`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => void remove(row.id),
    })
  }

  // -- Filtering -------------------------------------------------------------
  const filtered = items.filter((r) => {
    if (slaFilter && (r.appliesToSlaPolicyId ?? '') !== slaFilter) return false
    if (triggerFilter && r.triggerType !== triggerFilter) return false
    return true
  })

  // -- Grouping by SLA policy ------------------------------------------------
  const groups = new Map<string, EscalationRule[]>()
  for (const rule of filtered) {
    const key = rule.appliesToSlaPolicyId ?? UNSCOPED
    const bucket = groups.get(key) ?? []
    bucket.push(rule)
    groups.set(key, bucket)
  }
  const groupKeys = [...groups.keys()].sort((a, b) => {
    if (a === UNSCOPED) return 1
    if (b === UNSCOPED) return -1
    return a.localeCompare(b)
  })

  function slaName(id: string): string {
    return slaPolicies.find((p) => p.id === id)?.name ?? 'Unknown SLA'
  }

  const columns: ColumnDef<EscalationRule>[] = [
    { label: 'Name', accessor: 'name' },
    {
      label: 'Trigger',
      accessor: (r) => TRIGGER_LABELS[r.triggerType],
    },
    {
      label: 'Threshold',
      accessor: (r) => {
        const t = thresholdOf(r)
        return t == null ? '—' : `${t}%`
      },
    },
    {
      label: 'Actions',
      accessor: (r) => summariseActions(r.actions),
    },
    {
      label: 'Active',
      accessor: (r) => (
        <InputSwitch
          checked={r.isActive}
          onChange={(e) => void update(r.id, { isActive: Boolean(e.value) })}
        />
      ),
    },
  ]

  const defaultValues: RuleFormValues = editing
    ? {
        name: editing.name,
        appliesToSlaPolicyId: editing.appliesToSlaPolicyId ?? '',
        triggerType: editing.triggerType,
        thresholdPct:
          editing.triggerType === 'resolution_breach'
            ? (editing.resolutionThresholdPct ?? 80)
            : (editing.firstResponseThresholdPct ?? 80),
        isActive: editing.isActive,
        sortOrder: editing.sortOrder,
        webhookUrl: editing.webhookUrl ?? '',
        webhookSecret: '',
      }
    : copySource
      ? {
          name: `Copy of ${copySource.name}`,
          appliesToSlaPolicyId: copySource.appliesToSlaPolicyId ?? '',
          triggerType: copySource.triggerType,
          thresholdPct:
            copySource.triggerType === 'resolution_breach'
              ? (copySource.resolutionThresholdPct ?? 80)
              : (copySource.firstResponseThresholdPct ?? 80),
          isActive: copySource.isActive,
          sortOrder: copySource.sortOrder,
          webhookUrl: copySource.webhookUrl ?? '',
          webhookSecret: '',
        }
      : {
          name: '',
          appliesToSlaPolicyId: '',
          triggerType: 'first_response_breach',
          thresholdPct: 80,
          isActive: true,
          sortOrder: 100,
          webhookUrl: '',
          webhookSecret: '',
        }

  const slaOptions = [
    { label: 'All SLAs', value: '' },
    ...slaPolicies.map((p) => ({ label: p.name, value: p.id })),
  ]

  return (
    <div className="p-4">
      <PageHeader
        title="Escalation Rules"
        description="SLA breach + custom-condition escalations, grouped by SLA policy."
        actions={<Button label="+ New Rule" onClick={openCreate} />}
      />

      {/* Filters */}
      <div className="mb-4 flex flex-wrap gap-4">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Filter by SLA</span>
                    <Select
            ariaLabel="Filter by SLA"
            size="sm"
            value={slaFilter}
            onChange={(v) => setSlaFilter(v)}
          >
            <option value="">All SLAs</option>
            {slaPolicies.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
                    </Select>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Filter by trigger</span>
                    <Select
            ariaLabel="Filter by trigger"
            size="sm"
            value={triggerFilter}
            onChange={(v) => setTriggerFilter(v)}
          >
            <option value="">All triggers</option>
            {TRIGGER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
                    </Select>
        </label>
      </div>

      {filtered.length === 0 ? (
        <ResourceTable<EscalationRule>
          columns={columns}
          rows={[]}
          loading={loading}
          actions={['edit', 'copy', 'delete']}
          onEdit={openEdit}
          onCopy={openCopy}
          onDelete={handleDelete}
          empty={{
            title: 'No escalation rules',
            description: 'Create a rule to escalate breaching conversations.',
            action: <Button label="+ New Rule" onClick={openCreate} />,
          }}
        />
      ) : (
        groupKeys.map((key) => (
          <section key={key} className="mb-6">
            <h2
              data-testid={`group-header-${key}`}
              className="mb-2 text-sm font-semibold text-gray-700"
            >
              {key === UNSCOPED ? 'All SLAs (unscoped)' : slaName(key)}
            </h2>
            <ResourceTable<EscalationRule>
              columns={columns}
              rows={groups.get(key) ?? []}
              loading={loading}
              actions={['edit', 'copy', 'delete']}
              onEdit={openEdit}
              onCopy={openCopy}
              onDelete={handleDelete}
              empty={{
                title: 'No escalation rules',
                description: 'Create a rule to escalate breaching conversations.',
              }}
            />
          </section>
        ))
      )}

      <ResourceDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        onSave={async () => {}}
        formRef={formRef}
        width="wide"
        title={editing ? `Edit Rule: ${editing.name}` : copySource ? `Copy Rule: ${copySource.name}` : 'New Escalation Rule'}
      >
        <ZodForm
          schema={RuleSchema}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          render={(methods) => {
            const watchedTrigger = methods.watch('triggerType') as TriggerType
            const watchedSlaId = methods.watch('appliesToSlaPolicyId') as string
            const watchedThreshold = methods.watch('thresholdPct') as number
            const selectedSla = slaPolicies.find((p) => p.id === watchedSlaId)
            const slaMinutes =
              selectedSla == null
                ? null
                : watchedTrigger === 'resolution_breach'
                  ? selectedSla.resolutionMinutes
                  : selectedSla.firstResponseMinutes
            return (
              <>
                <FormText name="name" label="Name" required autoFocus />
                <FormDropdown
                  name="appliesToSlaPolicyId"
                  label="Applies to SLA"
                  options={slaOptions}
                />
                <FormDropdown
                  name="triggerType"
                  label="Trigger"
                  options={[...TRIGGER_OPTIONS]}
                  required
                />

                {isThresholdTrigger(watchedTrigger) && (
                  <div className="mt-2">
                    <FormNumber
                      name="thresholdPct"
                      label="Threshold %"
                      min={1}
                      max={100}
                    />
                    <ThresholdVisualization
                      slaMinutes={slaMinutes}
                      thresholdPct={
                        Number.isFinite(watchedThreshold)
                          ? watchedThreshold
                          : 1
                      }
                      triggerType={watchedTrigger}
                    />
                  </div>
                )}

                {watchedTrigger === 'custom_condition' && (
                  <div className="mt-4">
                    <h3 className="mb-2 text-sm font-semibold">Conditions</h3>
                    <RuleBuilder
                      value={conditions}
                      onChange={setConditions}
                      fields={fields}
                    />
                  </div>
                )}

                <div className="mt-4">
                  <h3 className="mb-2 text-sm font-semibold">Actions</h3>
                  <EscalationActionPicker
                    value={actions}
                    onChange={setActions}
                  />
                </div>

                {/* Webhook section — S2 T13 */}
                <div className="mt-4 rounded border border-gray-200 p-3">
                  <h3 className="mb-2 text-sm font-semibold">Webhook (optional)</h3>
                  <p className="mb-3 text-xs text-gray-500">
                    When this rule fires, KC3 will POST an HMAC-signed payload to the
                    configured URL. The secret is stored encrypted and never shown again.
                  </p>
                  <FormText name="webhookUrl" label="Webhook URL" />
                  <FormPassword
                    name="webhookSecret"
                    label={
                      editing?.hasWebhookSecret
                        ? 'Webhook Secret (leave blank to keep current)'
                        : 'Webhook Secret'
                    }
                  />
                  {editing?.hasWebhookSecret && (
                    <p className="mt-1 text-xs text-green-700">
                      <i className="pi pi-lock mr-1" aria-hidden />
                      Secret stored (write-only)
                    </p>
                  )}
                </div>

                <FormSwitch name="isActive" label="Active" />
                <FormNumber name="sortOrder" label="Sort order" />

                <button
                  type="submit"
                  className="mt-4 rounded bg-[#16273F] px-4 py-2 text-white"
                >
                  Save
                </button>
              </>
            )
          }}
        />
      </ResourceDrawer>
    </div>
  )
}

// Re-export for tests / external consumers.
export { TRIGGER_OPTIONS }
